import { useEffect, useState, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_KEY = "paydayrelay.userEmail";
const STATE_KEY = "paydayrelay.userState";
const STATE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Reads the persisted user email from localStorage.
 * Returns null if not set or invalid.
 */
export function getStoredEmail() {
  try {
    const e = localStorage.getItem(EMAIL_KEY);
    return e && e.includes("@") ? e : null;
  } catch {
    return null;
  }
}

/**
 * Persists the user email to localStorage so future visits recognize them.
 * Call this any time the app confirms a user's identity, Income Map submit,
 * SheetSetup token landing, purchase confirmation.
 */
export function rememberUser(email) {
  if (!email || !email.includes("@")) return;
  try {
    localStorage.setItem(EMAIL_KEY, email.toLowerCase().trim());
  } catch { /* noop */ }
}

/**
 * Clear stored identity (e.g., "sign out" from the returning-visitor experience).
 */
export function forgetUser() {
  try {
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(STATE_KEY);
  } catch { /* noop */ }
}

function readCachedState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.cachedAt || Date.now() - parsed.cachedAt > STATE_TTL_MS) return null;
    return parsed.state;
  } catch {
    return null;
  }
}

function writeCachedState(state) {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify({ state, cachedAt: Date.now() }));
  } catch { /* noop */ }
}

/**
 * Central hook for state-aware pages.
 * Returns { state, loading, refresh, forget }.
 *   - state: the /api/user-state response or null (never-visited)
 *   - loading: true during the first API refresh
 *   - refresh(): force a re-fetch
 *   - forget(): clear stored identity and state
 */
// Once we know this browser has no session, later mounts skip the round trip.
let anonymousThisSession = false;

export default function useUserState() {
  const [state, setState] = useState(() => readCachedState());
  const [loading, setLoading] = useState(false);

  const fetchState = useCallback(async (force = false) => {
    if (anonymousThisSession && !force) {
      setState(null);
      return;
    }
    setLoading(true);
    try {
      // The signed-in session decides whose state comes back. Never a query param.
      const res = await fetch(`${API}/api/user-state`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        anonymousThisSession = false;
        if (data?.email) rememberUser(data.email);
        setState(data);
        writeCachedState(data);
      } else if (res.status === 401 || res.status === 403) {
        anonymousThisSession = true;
        setState(null);
      }
    } catch { /* offline, keep cached state */ }
    finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  return {
    state,
    loading,
    refresh: () => fetchState(true),
    forget: () => {
      forgetUser();
      setState(null);
    },
  };
}
